import type { NoveltyCalcResponse } from '@/api';
import {
  formatFactorField,
  formatIndex,
  formatRm,
} from '@/components/table/table-format-helpers';
import { useNotification } from '@/hooks/use-notification';
import { themeColors } from '@/theme/colors';
import EditableInput from './editable-input';
import SummaryTableCell from './summary-table-cell';
import SummaryTableHeader from './summary-table-header';

type PriceCalcSummaryTableProps = {
  simulationName: string;
  onSimulationNameChange: (name: string) => void;
  calculatePriceResponse: NoveltyCalcResponse;
  onRerun: (lpIndexOverride: number) => void;
};

const PriceCalcSummaryTable = ({
  simulationName,
  onSimulationNameChange,
  calculatePriceResponse,
  onRerun,
}: PriceCalcSummaryTableProps) => {
  const response = calculatePriceResponse.resultsSummary;
  const showNotification = useNotification();

  const handleTargetChange = (newValue: string | number) => {
    const parsedTarget = Number(String(newValue).replace(',', '.'));
    if (!Number.isFinite(parsedTarget)) {
      showNotification({
        type: 'error',
        title: 'Invalid target value',
        description: 'Please enter a valid numeric value.',
      });
      return;
    }
    onRerun(parsedTarget);
  };

  const formatIndexCell = (value: number | null | undefined) =>
    value ? formatIndex(value) : '-';

  const formatFmc = (value: number | null | undefined) =>
    value ? formatFactorField(value) : '-';

  const formatRetailMargin = (value: number | null | undefined) =>
    value ? formatRm(value) : '-';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <span className="heading-md">Results</span>
        <EditableInput
          value={simulationName}
          onChange={(newName) => onSimulationNameChange(String(newName))}
        />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(370px, 6fr) 100px 130px 130px 130px 100px 130px',
        }}
      >
        <span
          style={{
            borderBottom: `1px solid ${themeColors.stroke.default}`,
            height: '48px',
          }}
        />
        <SummaryTableHeader
          text="Novelties"
          style={{
            gridColumnStart: 2,
          }}
        />
        <SummaryTableHeader text={'Novelties 1HY'} />
        <SummaryTableHeader text={'Novelties 2HY'} />
        <SummaryTableHeader text={'Carry overs'} />
        <SummaryTableHeader text={'Total'} />
        <SummaryTableHeader text={'Target'} />

        <SummaryTableCell
          topValue="List Price Index"
          bottomValue="Previous year"
          isHeading
        />
        <SummaryTableCell
          topValue={
            formatIndexCell(response?.listPriceIndex.novelties.current) ?? '-'
          }
          bottomValue={
            formatIndexCell(response?.listPriceIndex.novelties.previousYear) ??
            '-'
          }
        />
        <SummaryTableCell
          topValue={
            formatIndexCell(response?.listPriceIndex.novelties1Hy.current) ??
            '-'
          }
          bottomValue={
            formatIndexCell(
              response?.listPriceIndex.novelties1Hy.previousYear,
            ) ?? '-'
          }
        />
        <SummaryTableCell
          topValue={
            formatIndexCell(response?.listPriceIndex.novelties2Hy.current) ??
            '-'
          }
          bottomValue={
            formatIndexCell(
              response?.listPriceIndex.novelties2Hy.previousYear,
            ) ?? '-'
          }
        />
        <SummaryTableCell
          topValue={
            formatIndexCell(response?.listPriceIndex.carryOvers.current) ?? '-'
          }
          bottomValue={
            formatIndexCell(response?.listPriceIndex.carryOvers.previousYear) ??
            '-'
          }
        />
        <SummaryTableCell
          topValue={
            formatIndexCell(response?.listPriceIndex.total.current) ?? '-'
          }
          bottomValue={
            formatIndexCell(response?.listPriceIndex.total.previousYear) ?? '-'
          }
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            borderBottom: `1px solid ${themeColors.stroke.subtle}`,
          }}
        >
          <EditableInput
            value={response?.target ?? 0}
            onChange={handleTargetChange}
          />
        </div>

        <SummaryTableCell
          topValue="List Price FMC Factor"
          bottomValue="Previous year"
          isHeading
        />
        <SummaryTableCell
          topValue={formatFmc(response?.listPriceFmcFactor.novelties.current)}
          bottomValue={formatFmc(
            response?.listPriceFmcFactor.novelties.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatFmc(
            response?.listPriceFmcFactor.novelties1Hy.current,
          )}
          bottomValue={formatFmc(
            response?.listPriceFmcFactor.novelties1Hy.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatFmc(
            response?.listPriceFmcFactor.novelties2Hy.current,
          )}
          bottomValue={formatFmc(
            response?.listPriceFmcFactor.novelties2Hy.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatFmc(response?.listPriceFmcFactor.carryOvers.current)}
          bottomValue={formatFmc(
            response?.listPriceFmcFactor.carryOvers.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatFmc(response?.listPriceFmcFactor.total.current)}
          bottomValue={formatFmc(
            response?.listPriceFmcFactor.total.previousYear,
          )}
        />
        <div
          style={{
            padding: '8px',
            borderBottom: `1px solid ${themeColors.stroke.subtle}`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span className="body-md-bold">{response?.targetFmc}</span>
        </div>

        <SummaryTableCell
          topValue="Retail Margin"
          bottomValue="Previous year"
          isHeading
        />
        <SummaryTableCell
          topValue={formatRetailMargin(
            response?.retailMargin.novelties.current,
          )}
          bottomValue={formatRetailMargin(
            response?.retailMargin.novelties.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatRetailMargin(
            response?.retailMargin.novelties1Hy.current,
          )}
          bottomValue={formatRetailMargin(
            response?.retailMargin.novelties1Hy.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatRetailMargin(
            response?.retailMargin.novelties2Hy.current,
          )}
          bottomValue={formatRetailMargin(
            response?.retailMargin.novelties2Hy.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatRetailMargin(
            response?.retailMargin.carryOvers.current,
          )}
          bottomValue={formatRetailMargin(
            response?.retailMargin.carryOvers.previousYear,
          )}
        />
        <SummaryTableCell
          topValue={formatRetailMargin(response?.retailMargin.total.current)}
          bottomValue={formatRetailMargin(
            response?.retailMargin.total.previousYear,
          )}
        />
        <div
          style={{ borderBottom: `1px solid ${themeColors.stroke.subtle}` }}
        />
      </div>
    </div>
  );
};
export default PriceCalcSummaryTable;
